import React from "react";
import {
  Navbar as TwNavbar,
  Typography,
  IconButton,
  Input,
  Badge,
  Collapse,
  Spinner,
} from "@material-tailwind/react";
import {
  ShoppingCartIcon,
  InformationCircleIcon,
  UserIcon,
  XMarkIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { BasketDrawer } from "../../Pages/MainPage/Components/Drawer/BasketDrawer";
import { stockiestPortalController } from "../Controllers/StockiestPortalController";
import { filteringController } from "../../Pages/MainPage/Controllers/FilteringController";
import Alert from "./Alert";
import { userAccountController } from "../Controllers/UserAccountController";
import { loginSignupController } from "./Standalone/LoginOrSignup/LoginSignupController";
import { LoginSignupCard } from "./Standalone/LoginOrSignup/LoginSignupCard";
import { alertController } from "../Controllers/AlertController";

type SidebarButton = {
  text: "latest" | "upcoming" | "extras";
  onClick: (navigate: (url: string) => void) => void;
};

const sidebarButtons: SidebarButton[] = [
  {
    text: "latest",
    onClick: navigate => navigate("latest"),
  },
  {
    text: "upcoming",
    onClick: navigate => navigate("upcoming"),
  },
  {
    text: "extras",
    onClick: navigate => {
      if (userAccountController.isLoggedIn) {
        navigate("extras");
        return;
      }
      alertController.displayAlert(
        "Login required",
        "You have to be logged in in order to view this page.",
        "warning",
        4000
      );
    },
  }
];

type NavbarProps = {
  isSearchable: boolean;
  isBasketIconEnabled: boolean;
  isAccountIconEnabled: boolean;
  isInline: boolean;
};

export const Navbar = observer(
  ({
    isSearchable,
    isBasketIconEnabled,
    isAccountIconEnabled,
    isInline,
  }: NavbarProps) => {
    const [openNav, setOpenNav] = React.useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
    const navigate = useNavigate();

    const handleWindowResize = () => {
      if (window.innerWidth >= 552) {
        setOpenNav(false);
      }
      if (window.innerWidth >= 1024) {
        setIsSearchExpanded(false);
      }
    };

    React.useEffect(() => {
      window.addEventListener("resize", handleWindowResize);
      return () => window.removeEventListener("resize", handleWindowResize);
    }, []);

    return (
      <>
        <div
          className={classNames("top-0 z-[90] w-full", {
            fixed: !isInline,
            sticky: isInline,
          })}
        >
          <TwNavbar
            className="relative xsm:h-max max-w-full rounded-none py-2 px-2 sm:px-4 lg:px-8 lgh:py-4 border-b-2 bg-cover"
            style={{
              backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.6)), url('/images/header-bg.png')"
            }}
            color="transparent"
            fullWidth={false}
          >
            <div className="flex gap-1 items-center justify-between text-white flex-nowrap">
              <div
                className={classNames(
                  "flex flex-row items-center justify-center cursor-pointer gap-1 sm:gap-4 flex-shrink-0 mdh:h-24",
                  {
                    "opacity-0 pointer-events-none absolute": isSearchExpanded,
                    "opacity-100 relative": !isSearchExpanded
                  }
                )}
                onClick={() => {
                  if (!isSearchExpanded) {
                    navigate("/");
                  }
                }}
              >
                <img
                  src="/images/flying-mag-logo.png"
                  className="py-1 mdh:py-0 h-[40px] w-[57px] sm:h-[48px] sm:w-[68px] mdh:h-[60px] mdh:w-[99px]"
                />
                <div className="flex-col h-full flex justify-center gap-[3px] md:gap-1">
                  <Typography className="block antialiased font-sans font-light text-inherit mdh:mr-4 text-sm mdh:text-2xl xl:text-4xl uppercase">
                    Newsstand Distribution
                  </Typography>
                  <Typography className="mdh:mr-2 text-[10px] sm:text-xs mdh:text-sm xl:text-base leading-tight max-w-[160px] sm:max-w-[unset]">
                    Independent Magazine Delivery for Merchants Worldwide
                  </Typography>
                </div>
              </div>

              {isSearchable && (
                <div className={classNames(
                  "flex items-center justify-center gap-2 transition-all duration-300 mdh:my-[10px]",
                  {
                    "flex-1 mx-2": isSearchExpanded,
                    "ml-auto": !isSearchExpanded
                  }
                )}>
                  {isSearchExpanded && (
                    <div className="relative my-0.5 flex items-center gap-1 flex-grow mdh:hidden">
                      <Input
                        size="lg"
                        color="white"
                        label="Search titles"
                        className="pr-8 placeholder-shown:border-white placeholder-shown:border-t-white"
                        name="search"
                        autoFocus
                        value={filteringController.searchValue}
                        onChange={event => {
                          const value = event.target.value;

                          filteringController.onSearchValueChange(value);

                          if (value.length > 0) {
                            const alreadyOnSearch = window.location.pathname === '/search';
                            navigate(`/search?query=${encodeURIComponent(value)}`, { replace: alreadyOnSearch });
                          } else {
                            navigate(filteringController.lastNonSearchPath, { replace: true });
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            if (!filteringController.searchValue) {
                              setIsSearchExpanded(false);
                            }
                          }, 150);
                        }}
                        containerProps={{ className: "!min-w-[unset] !w-full" }}
                      />
                      <IconButton
                        variant="text"
                        color="white"
                        onClick={() => {
                          setIsSearchExpanded(false);
                          filteringController.onSearchValueChange("");
                          navigate(filteringController.lastNonSearchPath);
                        }}
                        className="h-10 w-10 flex-shrink-0"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </IconButton>
                    </div>
                  )}

                  {!isSearchExpanded && (
                    <IconButton
                      variant="text"
                      color="white"
                      onClick={() => setIsSearchExpanded(true)}
                      className="mdh:hidden flex-shrink-0 h-6 w-6 md:h-12 md:w-12"
                    >
                      <MagnifyingGlassIcon className="h-6 w-6 md:h-8 md:w-8" />
                    </IconButton>
                  )}

                  <div className="relative hidden mdh:flex gap-1 md:mr-2 min-w-[120px] max-w-[300px] flex-grow">
                    <Input
                      size="lg"
                      color="white"
                      label="Search titles"
                      className="pr-8 placeholder-shown:border-white placeholder-shown:border-t-white"
                      name="search"
                      value={filteringController.searchValue}
                      onChange={event => {
                        const value = event.target.value;

                        filteringController.onSearchValueChange(value);

                        if (value.length > 0) {
                          const alreadyOnSearch = window.location.pathname === '/search';
                          navigate(`/search?query=${encodeURIComponent(value)}`, { replace: alreadyOnSearch });
                        } else {
                          navigate(filteringController.lastNonSearchPath, { replace: true });
                        }
                      }}
                      containerProps={{ className: "!min-w-[unset] !w-full" }}
                    />
                  </div>
                </div>
              )}

              <div className={classNames(
                "relative mdh:gap-2",
                {
                  "hidden md:flex": isSearchable && !isSearchExpanded,
                  "hidden": isSearchable && isSearchExpanded,
                  "hidden xsm:flex": !isSearchable
                }
              )}>
                <NavMenus
                  isBasketIconEnabled={isBasketIconEnabled}
                  isBasketDataLoading={stockiestPortalController.isBasketDataLoading}
                  isAccountIconEnabled={isAccountIconEnabled}
                  isAccountDataLoading={userAccountController.isUserAccountDataLoading}
                />
              </div>

              <IconButton
                variant="text"
                className={classNames(
                  "h-6 w-6 min-w-[24px] flex-shrink-0",
                  {
                    "md:hidden": isSearchable && !isSearchExpanded,
                    "hidden": isSearchable && isSearchExpanded,
                    "xsm:hidden": !isSearchable
                  }
                )}
                ripple={false}
                color="white"
                onClick={() => setOpenNav(!openNav)}
              >
                {openNav ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </IconButton>

              <div className={classNames(
                "absolute right-0",
                {
                  "md:hidden": isSearchable && !isSearchExpanded,
                  "hidden": isSearchable && isSearchExpanded,
                  "xsm:hidden": !isSearchable
                }
              )}>
                <div className="absolute -right-2 top-8 z-[10] pointer-events-none">
                  <Collapse
                    open={openNav}
                    className={`pointer-events-auto bg-main/95 backdrop-blur-md rounded-b border-x border-light-gray ${openNav ? "border-y" : ""}`}
                  >
                    <div className="p-4 flex flex-col gap-2">
                      <NavMenus
                        isBasketIconEnabled={isBasketIconEnabled}
                        isBasketDataLoading={stockiestPortalController.isBasketDataLoading}
                        isAccountIconEnabled={isAccountIconEnabled}
                        isAccountDataLoading={userAccountController.isUserAccountDataLoading}
                        isColumn={true}
                      />
                    </div>
                  </Collapse>
                </div>
              </div>

            </div>
            <BasketDrawer />
            <LoginSignupCard />
          </TwNavbar>

          {stockiestPortalController.showIssueNavigation &&
            <div className="bg-main/60 backdrop-blur-sm shadow-[1px_0_4px_1px_rgb(21,24,29,0.3)] flex flex-col md:flex-row items-center justify-between px-3 md:px-8">
              <NavTabs />
            </div>
          }
        </div>
        <Alert />
      </>
    );
  }
);

const NavTabs = observer(() => {
  const navigate = useNavigate();

  const chooseVariant = (button: SidebarButton) => {
    return filteringController.currentTab === button.text
      ? "text-white border-b-[6px] border-purple font-bold"
      : "text-gray-300 hover:text-white";
  };

  return (
    <div className="w-full animate-fadeUp">
      <div className="flex justify-center md:justify-start items-start gap-6 py-1 mdh:pt-2 overflow-x-hidden">
        {sidebarButtons.map(button => (
          <button
            key={button.text}
            onClick={() => button.onClick(navigate)}
            className={classNames(
              "capitalize text-xl smh:text-2xl text-white transition duration-150 ease-in-out whitespace-nowrap",
              chooseVariant(button)
            )}
          >
            {button.text}
          </button>
        ))}
      </div>
    </div>
  );
});

type NavMenusProps = {
  isBasketIconEnabled: boolean;
  isBasketDataLoading: boolean;
  isAccountIconEnabled: boolean;
  isAccountDataLoading: boolean;
  isColumn?: boolean
};

const NavMenus = observer(
  ({
    isBasketIconEnabled,
    isBasketDataLoading,
    isAccountIconEnabled,
    isAccountDataLoading,
    isColumn = false
  }: NavMenusProps) => {
    const navigate = useNavigate();

    const onUserIconClick = () => {
      if (userAccountController.isLoggedIn) {
        navigate("/account");
        return;
      }
      loginSignupController.setCurrentWindow("LOGIN");
      loginSignupController.setLoginSignupWindowVisible(true);
    };

    const onInfoIconClick = () => navigate("/about");

    const onBasketIconClick = () => {
      if (!userAccountController.isLoggedIn) {
        alertController.displayAlert(
          "Login required",
          "You have to be logged in in order to view your basket.",
          "warning",
          4000
        );
        loginSignupController.setCurrentWindow("LOGIN");
        loginSignupController.setLoginSignupWindowVisible(true);
        return;
      }
      stockiestPortalController.toggleBasketDrawerOpen(true);
    };

    const getInitials = (name: string): string => {
      if (!name) return "";
      const words = name.trim().split(/\s+/);
      const initials = words.slice(0, 2).map(w => w[0].toUpperCase()).join("");
      return initials;
    };

    const initials = userAccountController.stockist?.stockistName ? getInitials(userAccountController.stockist.stockistName) : "";

    return (
      <div className={`flex flex-${isColumn ? "col" : "row"} gap-2 items-center justify-end w-full h-full`}>
        <div className="h-10 w-10 relative">
          {isBasketIconEnabled ? (
            <>
              <div
                className={classNames("absolute", {
                  "animate-fadeIn": isBasketDataLoading,
                  "animate-fadeOut": !isBasketDataLoading,
                })}
              >
                <Spinner className="h-10 w-10" />
              </div>
              <div
                className={classNames("absolute", {
                  "animate-fadeIn": !isBasketDataLoading,
                  "animate-fadeOut": isBasketDataLoading,
                })}
              >
                <Badge
                  content={`£${stockiestPortalController.totalPriceInclDiscountInPounds.toFixed(
                    0
                  )}`}
                  invisible={
                    stockiestPortalController.totalPriceInclDiscountInPounds <= 0
                  }
                >
                  <IconButton
                    variant="text"
                    color="white"
                    onClick={onBasketIconClick}
                  >
                    <ShoppingCartIcon className="h-8 w-8" />
                  </IconButton>
                </Badge>
              </div>
            </>
          ) : (
            <IconButton variant="text" color="white" disabled>
              <ShoppingCartIcon className="h-8 w-8" />
            </IconButton>
          )}
        </div>

        <div className="h-10 w-10">
          <IconButton variant="text" color="white" onClick={onInfoIconClick}>
            <InformationCircleIcon className="h-8 w-8" />
          </IconButton>
        </div>

        <div className="h-10 w-10 relative flex items-center justify-center">
          {isAccountIconEnabled ? (
            <>
              <div
                className={classNames("absolute", {
                  "animate-fadeIn": isAccountDataLoading,
                  "animate-fadeOut": !isAccountDataLoading,
                })}
              >
                <Spinner className="h-10 w-10" />
              </div>
              <div
                className={classNames("absolute", {
                  "animate-fadeIn": !isAccountDataLoading,
                  "animate-fadeOut": isAccountDataLoading,
                })}
              >{userAccountController.isLoggedIn && userAccountController.stockist && initials ?
                <div
                  className="flex items-center justify-center h-8 w-8 rounded-full bg-white text-black font-semibold hover:cursor-pointer hover:bg-gray-500 transition duration-150 ease-in-out"
                  onClick={onUserIconClick}
                >
                  {initials}
                </div> :
                <IconButton variant="text" color="white" onClick={onUserIconClick}>
                  <UserIcon className="h-8 w-8" />
                </IconButton>
                }
              </div>
            </>
          ) : (
            userAccountController.isLoggedIn && userAccountController.stockist && initials ?
              <div
                className="flex items-center justify-center h-8 w-8 rounded-full bg-gray-400 text-black font-semibold transition duration-150 ease-in-out"
              >
                {initials}
              </div> :
              <IconButton variant="text" color="white">
                <UserIcon className="h-8 w-8" />
              </IconButton>
          )}
        </div>
      </div>
    );
  }
);
